import { useCallback, useEffect, useRef, useState } from 'react';
import { useImmerAtom } from 'jotai-immer';
import { dataMonitorPageStateAtom } from '../datamonitor_list_atoms';
import datamonitorRepo, { HeldOrderAction } from '../../data/datamonitorRepo';
import { ngDelegate } from '../../../../core/services/ng.delegate';

type UseWacOrderingReturn = {
    // null until the first status read; true = ordering running, false = stopped/held
    wacOrderingStatus: boolean | null;
    waiting: boolean;
    stopWacOrders: () => Promise<void>;
    restartWacOrders: (heldAction: HeldOrderAction) => Promise<void>;
};

// VERITYSA WAC ordering circuit breaker. Status lives on the page atom so this control
// and the System tab stay in sync; the System refresh also updates the same atom.
export const useWacOrdering = (): UseWacOrderingReturn => {
    const [state, setState] = useImmerAtom(dataMonitorPageStateAtom);
    const [waiting, setWaiting] = useState(false);
    const waitingRef = useRef(false);

    const setStatus = useCallback(
        (val: boolean): void => {
            setState(draft => {
                draft.wacOrderingStatus = val;
            });
        },
        [setState],
    );

    // seed status on mount if the System tab hasn't already loaded it
    useEffect(() => {
        if (state.wacOrderingStatus !== null) return;
        let active = true;
        datamonitorRepo
            .getWacOrderingStatus()
            .then(s => {
                if (active) setStatus(s);
            })
            .catch(() => {});
        return (): void => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stopWacOrders = useCallback(async (): Promise<void> => {
        // guard against double-submit while a request is in flight (legacy isWaiting)
        if (waitingRef.current) return;
        waitingRef.current = true;
        setWaiting(true);
        try {
            await datamonitorRepo.stopWacOrders();
            setStatus(false);
            ngDelegate.ok('Stop WAC Orders', 'WAC orders are now stopped.');
        } catch {
            // api error already surfaced by apiSvc
        } finally {
            waitingRef.current = false;
            setWaiting(false);
        }
    }, [setStatus]);

    const restartWacOrders = useCallback(
        async (heldAction: HeldOrderAction): Promise<void> => {
            if (waitingRef.current) return;
            waitingRef.current = true;
            setWaiting(true);
            try {
                await datamonitorRepo.restartWacOrders(heldAction);
                setStatus(true);
                ngDelegate.ok('Restart WAC Orders', 'WAC Ordering is now re-started.');
            } catch {
                // api error already surfaced by apiSvc
            } finally {
                waitingRef.current = false;
                setWaiting(false);
            }
        },
        [setStatus],
    );

    return {
        wacOrderingStatus: state.wacOrderingStatus,
        waiting,
        stopWacOrders,
        restartWacOrders,
    };
};
